/**
 * Client-side cycle detection for the task dependency graph (addendum §7).
 *
 * Input: a list of tasks each with `taskId` and `dependsOn` (parent task IDs).
 * Returns:
 *   - `wouldCreateCycle(...)` — simulates a proposed change and reports
 *     whether the resulting graph contains a cycle (DFS with white/gray/black
 *     coloring). Used to block a `dependsOn` save before writing.
 *   - `findCycleMembers(...)` — returns all task IDs that participate in any
 *     cycle in the current graph. Used to cross-check `TaskDoc.cycleDetected`.
 */

export interface TaskGraphNode {
  taskId: string;
  dependsOn: readonly string[];
}

type AdjacencyList = ReadonlyMap<string, readonly string[]>;

function buildAdjacency(
  tasks: readonly TaskGraphNode[],
  override?: { taskId: string; dependsOn: readonly string[] },
): AdjacencyList {
  const map = new Map<string, readonly string[]>();
  for (const t of tasks) map.set(t.taskId, t.dependsOn);
  if (override) map.set(override.taskId, override.dependsOn);
  return map;
}

/**
 * Returns true if the graph contains at least one cycle.
 * Runs iterative DFS with white/gray/black coloring to bound recursion depth.
 */
function hasCycleInGraph(adj: AdjacencyList): boolean {
  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const color = new Map<string, number>();
  for (const id of adj.keys()) color.set(id, WHITE);

  for (const start of adj.keys()) {
    if (color.get(start) !== WHITE) continue;
    const stack: Array<{ id: string; idx: number }> = [{ id: start, idx: 0 }];
    color.set(start, GRAY);

    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!;
      const parents = adj.get(frame.id) ?? [];
      if (frame.idx >= parents.length) {
        color.set(frame.id, BLACK);
        stack.pop();
        continue;
      }
      const next = parents[frame.idx]!;
      frame.idx += 1;
      if (!adj.has(next)) continue; // dangling ref, not a cycle
      const c = color.get(next);
      if (c === GRAY) return true;
      if (c === WHITE) {
        color.set(next, GRAY);
        stack.push({ id: next, idx: 0 });
      }
    }
  }
  return false;
}

/**
 * Simulate editing `taskId.dependsOn` to `proposedDependsOn` and report
 * whether the edit would introduce a cycle.
 */
export function wouldCreateCycle(
  tasks: readonly TaskGraphNode[],
  taskId: string,
  proposedDependsOn: readonly string[],
): boolean {
  // Self-reference is always a cycle.
  if (proposedDependsOn.includes(taskId)) return true;
  const adj = buildAdjacency(tasks, {
    taskId,
    dependsOn: proposedDependsOn,
  });
  return hasCycleInGraph(adj);
}

/**
 * Simulate adding a dependency edge `childId` depends on `parentId`
 * (i.e. parent must complete before child). Returns true if adding the edge
 * creates a cycle.
 */
export function wouldCreateCycleByAddingEdge(
  tasks: readonly TaskGraphNode[],
  childId: string,
  parentId: string,
): boolean {
  if (childId === parentId) return true;
  const child = tasks.find((t) => t.taskId === childId);
  const nextDeps = child
    ? [...new Set([...child.dependsOn, parentId])]
    : [parentId];
  return wouldCreateCycle(tasks, childId, nextDeps);
}

/**
 * Tarjan's SCC algorithm; returns an array of task-id groups where each
 * group has size > 1 (or a self-loop). Used only as a cross-check —
 * authoritative cycle flagging happens in Cloud Functions.
 */
export function findCycleMembers(tasks: readonly TaskGraphNode[]): string[] {
  const adj = buildAdjacency(tasks);
  const indexMap = new Map<string, number>();
  const lowlink = new Map<string, number>();
  const onStack = new Set<string>();
  const stack: string[] = [];
  const sccs: string[][] = [];
  let index = 0;

  const strongConnect = (v: string): void => {
    indexMap.set(v, index);
    lowlink.set(v, index);
    index += 1;
    stack.push(v);
    onStack.add(v);

    for (const w of adj.get(v) ?? []) {
      if (!adj.has(w)) continue;
      if (!indexMap.has(w)) {
        strongConnect(w);
        lowlink.set(v, Math.min(lowlink.get(v)!, lowlink.get(w)!));
      } else if (onStack.has(w)) {
        lowlink.set(v, Math.min(lowlink.get(v)!, indexMap.get(w)!));
      }
    }

    if (lowlink.get(v) === indexMap.get(v)) {
      const comp: string[] = [];
      while (true) {
        const w = stack.pop()!;
        onStack.delete(w);
        comp.push(w);
        if (w === v) break;
      }
      if (
        comp.length > 1 ||
        (comp.length === 1 && (adj.get(comp[0]!) ?? []).includes(comp[0]!))
      ) {
        sccs.push(comp);
      }
    }
  };

  for (const v of adj.keys()) {
    if (!indexMap.has(v)) strongConnect(v);
  }

  const result = new Set<string>();
  for (const comp of sccs) for (const id of comp) result.add(id);
  return [...result];
}
